/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, UncontrolledPagination, ITelemetryContent, getTelemetryObject } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { IRequestContext } from '@msdyn365-commerce/core';
import * as React from 'react';
import { getSimpleProductsAction, ProductInput, getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { IInvoiceDetailsViewProps } from '@msdyn365-commerce-modules/invoice-details/src/modules/invoice-details/./invoice-details';
import { IContext, IQueryResultSettings, SalesInvoiceLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ICocCInvoiceDetailsProps } from './coc-c-invoice-details.props.autogenerated';
import InvoiceDetailsPaymentSummary from './components/coc-c-invoice-details-payment-summary';
import { ICocCInvoiceDetailsData } from './coc-c-invoice-details.data';
import { InvoiceDetailsHeader } from './components/coc-c-invoice-details-header';
import { InvoiceDetailsProductDetailComponent } from './components/invoice-details-product-component';
import { IGetInvoiceDetailsRequest, IInvoiceDetailsEntity } from './../../customProxy/retailExtension/InvoiceDetailDataServiceEntities.g';
import { getInvoiceDetailsAsync } from '../../customProxy/retailExtension/InvoiceDetailDataActionExtension.g';

/**
 * TODO:These interfaces and class will be in a seprate file, due to time limitation we are keeping them here.
 */
export interface IInvoicePaymentDetails {
    totalDiscount?: number;
    subtotal?: number;
    totalCharges?: number;
    totalTax?: number;
    totalAmount?: number;
    currencyCode?: string;
}

export interface IInvoiceProductLine extends SalesInvoiceLine {
    primaryImageUrl?: string;
}

export interface SalesInvoiceCustom {
    RecordID?: number;
    SalesID?: string;
    InvoiceID?: string;
    TotalDiscount?: number;
    InvoiceSubTotal?: number;
    TotalCharges?: number;
    TotalTaxAmount?: number;
    ItemID?: string;
    ProductName?: string;
    FolioNumber?: string;
    SearchKey?: string;
    SearchKeyName?: string;
    Address?: string;
    CreatedBy?: string;
    CreatedDate?: string;
    OrignalSalesID?: string;
    RecID?: number;
    ExtensionProperties?: string[];
}

export interface SalesInvoiceArray {
    SalesInvoiceCustom?: SalesInvoiceCustom[];
}

export interface ProductComponetInput {
    primaryImageUrl?: string;
    altImageUrl?: string;
    productName?: string;
    searckKey?: string;
    FolioNumber?: string;
    SalesOrder?: string;
    CreatedDate?: Date;
    PlacedBy?: string;
    productPrice?: number;
    recordId: number;
}

export interface ProductHeaderInput {
    invoiceDate: Date | undefined;
    invoiceid: string;
    totalProductValue: number;
    shipping: number;
    totalTaxes: number;
    invoiceTotal: number;
    props: IInvoiceDetailsViewProps;
}

export const InvoiceDetailsView: React.FC<IInvoiceDetailsViewProps & ICocCInvoiceDetailsProps<ICocCInvoiceDetailsData>> = (
    props: IInvoiceDetailsViewProps & ICocCInvoiceDetailsProps<ICocCInvoiceDetailsData>
) => {
    const [invoiceDetailsState, setInvoiceDetailsState] = React.useState<SimpleProduct[]>();
    const {
        header,
        payInvoiceError,
        resources: { invoiceSubTitle }
    } = props;

    const telemetryContent: ITelemetryContent = getTelemetryObject(
        props.context.request.telemetryPageName!,
        props.friendlyName,
        props.telemetry
    );
    const _pageType: string | undefined = props.context.request.urlTokens.pageType;

    const [arr, setArr] = React.useState<ProductComponetInput[]>([]);
    const [pagination, setPagination] = React.useState<React.ReactNode | undefined>();
    const [invoiceDetailHeader, setInvoiceDetailHeader] = React.useState<ProductHeaderInput>();
    const [invoiceDetailsCustom, setInvoiceDetailCustom] = React.useState<IInvoiceDetailsEntity[]>();
    const itemsPerPage = props.config.itemsPerPage;

    const getInvoiceDetails: IGetInvoiceDetailsRequest = {
        InvoiceId: props.context.request.query?.invoiceId
    };

    async function dataction(input: IGetInvoiceDetailsRequest, context: IContext, top: number): Promise<IInvoiceDetailsEntity[]> {
        const invoiceID: IGetInvoiceDetailsRequest = { InvoiceId: props.context.request.query?.invoiceId };
        const topItems: number = itemsPerPage ? itemsPerPage : 10;
        const skip = Number(context.callerContext.requestContext.query?.skip);
        const queryResultSettingsValue: IQueryResultSettings = {
            Paging: { Top: topItems, Skip: skip || 0 }
        };
        const promise = await getInvoiceDetailsAsync({ callerContext: props.context.actionContext }, invoiceID, queryResultSettingsValue);
        return promise;
    }
    React.useEffect(() => {
        dataction(getInvoiceDetails, { callerContext: props.context.actionContext }, itemsPerPage ? itemsPerPage : 10)
            .then(res => {
                if (res === undefined || res === null) {
                    props.context.telemetry.error('No Response from Server');
                } else {
                    setInvoiceDetailCustom(res);
                }
            })
            .catch(error => {
                props.context.telemetry.error(`${error}`);
            });
    }, []);

    React.useEffect(() => {
        const { channelId } = props.context.actionContext.requestContext.apiSettings;

        _getProducts(invoiceDetailsCustom!, channelId)
            .then(value => {
                //console.log('VALUE', value);
                setInvoiceDetailsState(value);
            })
            .catch(error => {
                //console.log('ERROR', error);
                props.context.telemetry.error(`${error}`);
            });
    }, [invoiceDetailsCustom]);

    React.useEffect(() => {
        if (invoiceDetailsCustom === undefined || invoiceDetailsCustom === null) {
            props.context.telemetry.error('Something Went Wrong.');
        } else {
            const invoice = props.data.invoice;
            const invoiceDetailHeaderPropBuilder: ProductHeaderInput = {
                invoiceDate: undefined,
                invoiceid: '',
                totalProductValue: 0,
                shipping: 0,
                totalTaxes: 0,
                invoiceTotal: 0,
                props: props
            };
            invoiceDetailHeaderPropBuilder.invoiceTotal = invoice.result?.TotalAmount!;
            invoiceDetailHeaderPropBuilder.invoiceid = invoice.result?.Id!;
            invoiceDetailHeaderPropBuilder.totalProductValue = invoiceDetailsCustom[0].InvoiceSubTotal
                ? invoiceDetailsCustom[0].InvoiceSubTotal
                : 0;
            invoiceDetailHeaderPropBuilder.invoiceDate = invoice.result?.InvoiceDate;
            invoiceDetailHeaderPropBuilder.shipping = 0;
            invoiceDetailHeaderPropBuilder.invoiceTotal = invoice.result?.TotalAmount!;
            invoiceDetailHeaderPropBuilder.totalTaxes = invoiceDetailsCustom[0].TotalTaxAmount ? invoiceDetailsCustom[0].TotalTaxAmount : 0;
            setInvoiceDetailHeader(invoiceDetailHeaderPropBuilder);

            void Promise.all([
                invoiceDetailsCustom.map(item => {
                    let imageURL;
                    //let Price;
                    const altImageURL = getFallbackImageUrl(item.ItemID, props.context.actionContext.requestContext.apiSettings);
                    //console.log('STATE', invoiceDetailsState);
                    invoiceDetailsState?.map(line => {
                        if (line.RecordId === item.ProductRecID) {
                            imageURL = line.PrimaryImageUrl;
                            //Price = line.Price;
                        }
                    });

                    const arrinput: ProductComponetInput = {
                        primaryImageUrl: imageURL,
                        altImageUrl: altImageURL,
                        productName: item.ProductName,
                        searckKey: item.SearchKey,
                        FolioNumber: item.FolioNumber,
                        SalesOrder: item.OrignalSalesID,
                        CreatedDate: item.CreatedDate,
                        PlacedBy: item.CreatedBy,
                        productPrice: item.LineAmount,
                        recordId: item.ProductRecID!
                    };
                    return arrinput;
                }),
                props.data.listPageState.then(listPageState => {
                    if (_pageType === 'Category') {
                        listPageState.pageType = 'Category';
                    } else {
                        listPageState.pageType = 'Search';
                    }

                    const pageNumber = Number(props.context.request.query?.skip || 0);
                    const totalProductCount = invoiceDetailsCustom[0].ExtensionProperties?.find(item => item.Key === 'TotalRecords')?.Value
                        ?.IntegerValue;
                    listPageState.totalProductCount = totalProductCount ? totalProductCount : itemsPerPage;
                    listPageState.currentPageNumber = pageNumber / (itemsPerPage ? itemsPerPage : 10) + 1;
                    listPageState.activeProducts = invoiceDetailsCustom!;
                })
            ]).then(arrinput => {
                setArr(arrinput[0]);
                setPagination(_getPagination());
            });
        }
    }, [invoiceDetailsCustom, invoiceDetailsState]);

    async function _getProducts(invoiceLines: IInvoiceDetailsEntity[], channelId?: number): Promise<SimpleProduct[]> {
        //console.log('INPUT', invoiceLines);

        const productInputs = invoiceLines.map(
            product => new ProductInput(product.ProductRecID!, props.context.request.apiSettings, channelId)
        );

        const products = await getSimpleProductsAction(productInputs, props.context.actionContext);
        return products;
    }

    const _renderHeader = (): JSX.Element => {
        return (
            <InvoiceDetailsHeader
                className={'msc-invoice-details'}
                id={`${header.props.className}__${props.data.invoice.result?.Id}`}
                data={invoiceDetailHeader!}
                properties={props}
            />
        );
    };

    const _renderPaymentDetails = (): JSX.Element => {
        return (
            <InvoiceDetailsPaymentSummary
                className={`msc-invoice-details__payment-details`}
                id={`msc-invoice-details__payment-details__${props.data.invoice.result?.Id}`}
                data={invoiceDetailHeader!}
                properties={props}
            />
        );
    };

    const _getPagination = (): React.ReactNode => {
        const { context, data, resources } = props;
        const listPageState = data && data.listPageState && data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const skipCount = Number(props.context.request.query?.skip!) || 0;
        const totalItems = listPageState?.totalProductCount;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={_renderPrev(previousText)}
                nextText={_renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={telemetryContent}
            />
        );
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    const _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    const _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    /**
     * Gets the current URL of the page from the request context.
     * @param requestContext The request context.
     */
    const getCurrentUrl = (requestContext: IRequestContext): URL => {
        if (MsDyn365.isBrowser) {
            return new URL(window.location.href);
        }

        // NOTE: Typing on requestURL is incorrect
        if (requestContext.url.requestUrl.href) {
            return new URL(requestContext.url.requestUrl.href);
        }
        return new URL(JSON.stringify(requestContext.url.requestUrl));
    };

    return (
        <div>
            <Module {...props.moduleProps}>
                {invoiceDetailHeader && _renderHeader()}
                {invoiceDetailHeader && _renderPaymentDetails()}
                <h2 className='sub-title'>{invoiceSubTitle}</h2>
                {arr.length > 0 ? <InvoiceDetailsProductDetailComponent data={arr} resources={props.resources} properties={props} /> : null}
                {payInvoiceError}
            </Module>
            {pagination}
        </div>
    );
};

export default InvoiceDetailsView;
